import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";

import { AppleMap } from "capitalrx-components-2.0/lib/FindBestPriceTool";
import { LoadingGroup, LoadingSkeleton, Tag } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";

import {
  DrugPricesRequest,
  Prices,
  PricesPharmacy,
  PricesRejectInfo,
  SubmissionClarificationCode,
  useListDrugPricesQuery,
} from "@/App/api/drug";
import { Pharmacy, useListFavoritePharmaciesQuery } from "@/App/api/pharmacy";
import { ContentPanel, MapLocation, NoResults, PageWrapper, Pager } from "@/App/components";
import { PHARMACY_FILTERING_NPI_OPTION, basisOfReimbursement } from "@/App/constants/constants";
import { ExperienceContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { DrugHeader } from "@/App/pages/FindBestPrice/DrugHeader";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { extractNumber } from "@/App/utils/formatSelectorDrugQuantity";
import { formatDollars, getPharmacyDescription } from "@/App/utils/utils";

import { FindBestPriceMemberMessageModal } from "./FindBestPriceMemberMessageModal";
import { FindBestPriceRejectModal } from "./FindBestPriceRejectModal";
import { FindBestPriceResultsSection } from "./FindBestPriceResultsSection";
import { isCopayMaximizerProgramCheck } from "./utils";

const DEFAULT_COMMON_ELEMENTS_LIMIT = 6;

const SIX_ARRAY = [...Array(DEFAULT_COMMON_ELEMENTS_LIMIT).values()];

const SORT_OPTIONS = ["distance", "low_to_high"] as const;

const TRANSLATION_PATH = "FIND_BEST_PRICE.findBestPriceResultsPage";

type SortValues = (typeof SORT_OPTIONS)[number];

export const FindBestPriceResultsPage: FC = () => {
  const { t: translate } = useTranslation();
  const { drug_id: drugId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState(1);
  const [showingRejectModal, setShowingRejectModal] = useState(false);
  const [showingMemberMessageModal, setShowingMemberMessageModal] = useState(false);
  const [suppressableCodes, setSuppresableCodes] = useState([]);
  const [sortBy, setSortBy] = useState<SortValues>(null);
  const [firstPageResults, setFirstPageResults] = useState<number>(DEFAULT_COMMON_ELEMENTS_LIMIT);

  const { hideMailPharmacyOnFBPTool, hideOnlinePharmacyOnFBPTool, mailPharmacyIdentifier, onlinePharmacyIdentifier } =
    useContext(ExperienceContext);

  const navigate = useNavigateClient();

  const dosageForm = searchParams.get("dosage_form");
  const dosage = searchParams.get("dosage");
  const quantity = searchParams.get("quantity");
  const daysSupply = searchParams.get("days_supply");
  const hasShownCopayMaxMessage = searchParams.get("copayMaxModalShown");

  const location: { readonly latitude: string; readonly longitude: string } = JSON.parse(
    decodeURIComponent(searchParams.get("loc") || "{}"),
  );

  const submissionClarificationCode = useMemo(() => {
    try {
      return JSON.parse(searchParams.get("submission_clarification_code"));
    } catch (err) {
      console.log(err);
    }
    return null;
  }, [searchParams]);

  const setSubmissionClarificationCode = useCallback(
    (submissionClarificationCode: SubmissionClarificationCode | null) => {
      setSearchParams((prev) => {
        const upcoming = new URLSearchParams(prev);
        if (submissionClarificationCode == null) {
          upcoming.delete("submission_clarification_code");
        } else {
          upcoming.set("submission_clarification_code", JSON.stringify(submissionClarificationCode));
        }
        return upcoming;
      });
    },
    [setSearchParams],
  );

  const commonDrugPricesRequest: DrugPricesRequest = useMemo(() => {
    let offset = null;
    const excludedNpis = [];

    if (pageNumber !== 1 && firstPageResults !== DEFAULT_COMMON_ELEMENTS_LIMIT) {
      offset = (pageNumber - 1) * DEFAULT_COMMON_ELEMENTS_LIMIT - (DEFAULT_COMMON_ELEMENTS_LIMIT - firstPageResults);
    }

    if (!hideMailPharmacyOnFBPTool && !!mailPharmacyIdentifier) excludedNpis.push(mailPharmacyIdentifier);
    if (!hideOnlinePharmacyOnFBPTool && !!onlinePharmacyIdentifier) excludedNpis.push(onlinePharmacyIdentifier);

    return {
      drug_id: +drugId,
      dosage_form: dosageForm,
      dosage: dosage,
      quantity: +extractNumber(quantity),
      days_supply: +extractNumber(daysSupply),
      location: location,
      page_number: pageNumber,
      override_codes: suppressableCodes,
      submission_clarification_code: submissionClarificationCode,
      results_per_page: DEFAULT_COMMON_ELEMENTS_LIMIT,
      offset,
      excluded_npi_list: excludedNpis,
    };
  }, [
    dosageForm,
    dosage,
    quantity,
    daysSupply,
    location,
    drugId,
    pageNumber,
    suppressableCodes,
    submissionClarificationCode,
    firstPageResults,
  ]);

  const onlinePharmacyPricesRequest: DrugPricesRequest = useMemo(() => {
    const { page_number, results_per_page, offset, excluded_npi_list, ...drugPricesRequest } = commonDrugPricesRequest;

    return {
      ...drugPricesRequest,
      pharmacy_identifier: onlinePharmacyIdentifier,
      pharmacy_identifier_type: PHARMACY_FILTERING_NPI_OPTION,
    };
  }, [commonDrugPricesRequest, hideOnlinePharmacyOnFBPTool, onlinePharmacyIdentifier]);

  const mailPharmacyPricesRequest: DrugPricesRequest = useMemo(
    () => ({
      ...onlinePharmacyPricesRequest,
      pharmacy_identifier: mailPharmacyIdentifier,
      pharmacy_identifier_type: PHARMACY_FILTERING_NPI_OPTION,
    }),
    [onlinePharmacyPricesRequest, hideMailPharmacyOnFBPTool, mailPharmacyIdentifier],
  );

  const isEnabledMailPharmacySection = pageNumber == 1 && !hideMailPharmacyOnFBPTool && !!mailPharmacyIdentifier;
  const isEnabledOnlinePharmacySection = pageNumber == 1 && !hideOnlinePharmacyOnFBPTool && !!onlinePharmacyIdentifier;

  const {
    data: commonDrugPrices,
    isLoading: isLoadingCommonDrugPrices,
    isError: isLoadingCommonDrugsError,
  } = useListDrugPricesQuery(commonDrugPricesRequest);
  const {
    data: mailPharmacyPrices,
    isLoading: isLoadingMailPharmacyPrices,
    isError: isLoadingMailPharmacyError,
  } = useListDrugPricesQuery(mailPharmacyPricesRequest, isEnabledMailPharmacySection);
  const {
    data: onlinePharmacyPrices,
    isLoading: isLoadingOnlinePharmacyPrices,
    isError: isLoadingOnlinePharmacyError,
  } = useListDrugPricesQuery(onlinePharmacyPricesRequest, isEnabledOnlinePharmacySection);
  const { data: favoritePharmacies } = useListFavoritePharmaciesQuery();

  const getTotalCost = (price: Prices) => {
    const dispensingFeePaid = price?.claim?.response?.response_pricing?.dispensing_fee_paid;
    const ingredientCostPaid = price?.claim?.response?.response_pricing?.ingredient_cost_paid;

    if (!dispensingFeePaid || !ingredientCostPaid) return;

    return +dispensingFeePaid + +ingredientCostPaid;
  };

  const lowestPrice = useMemo(() => {
    const prices: Prices[] = [];

    if (commonDrugPrices?.prices?.length > 0) prices.push(...commonDrugPrices.prices);
    if (isEnabledMailPharmacySection && mailPharmacyPrices?.prices?.length > 0) prices.push(...mailPharmacyPrices.prices);

    if (prices?.length === 0) return;

    return prices
      ?.map((price) => getTotalCost(price))
      .reduce((min, priceCost) => (priceCost !== null && priceCost < min ? priceCost : min));
  }, [commonDrugPrices?.prices, mailPharmacyPrices?.prices, isEnabledMailPharmacySection]);

  const showOnlinePrices = useMemo(() => {
    if (!isEnabledOnlinePharmacySection) return false;

    if (onlinePharmacyPrices?.prices?.length === 0) return false;

    return onlinePharmacyPrices?.prices?.every((price) => {
      const cost = getTotalCost(price);

      // Make sure that online is the lowest in the page
      return price !== null && cost < lowestPrice;
    });
  }, [isEnabledOnlinePharmacySection, lowestPrice, onlinePharmacyPrices]);

  useEffect(() => {
    let results = DEFAULT_COMMON_ELEMENTS_LIMIT;

    if (pageNumber !== 1) return;

    if (showOnlinePrices) results--;

    if (isEnabledMailPharmacySection && mailPharmacyPrices?.prices?.length > 0) results--;

    setFirstPageResults(results);
  }, [
    pageNumber,
    showOnlinePrices,
    isEnabledMailPharmacySection,
    mailPharmacyPrices,
    isLoadingMailPharmacyPrices,
    isLoadingOnlinePharmacyPrices,
  ]);

  const rejectInfo = useMemo(() => {
    if (commonDrugPrices) {
      return commonDrugPrices.reject_info;
    }
    return { reject_message_list: [] } as PricesRejectInfo;
  }, [commonDrugPrices]);

  const memberMessages = useMemo(() => {
    if (commonDrugPrices) {
      return commonDrugPrices.custom_member_messages || [];
    } else {
      return [];
    }
  }, [commonDrugPrices]);

  const drug = useMemo(() => {
    return commonDrugPrices?.prices[0]?.drug;
  }, [commonDrugPrices]);

  const getDrugPriceDisplay = useCallback((drugPrice: Prices) => {
    const basisOfReimbursementCode = drugPrice.claim.response.response_pricing?.basis_of_reimbursement_determination;
    if (basisOfReimbursementCode && basisOfReimbursement[basisOfReimbursementCode] === "usual_and_customary") {
      return translate(`${TRANSLATION_PATH}.priceUnavailable`);
    } else {
      return formatDollars(drugPrice.claim.response?.response_pricing?.patient_pay_amount);
    }
  }, []);

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });

  const pharmacies: (PricesPharmacy & { readonly patient_pay_amount: string })[] = useMemo(() => {
    if (commonDrugPrices) {
      return commonDrugPrices.prices.map((p) => {
        return {
          ...p.pharmacy,
          patient_pay_amount: formatDollars(p?.claim?.response?.response_pricing?.patient_pay_amount || "0"),
        };
      });
    }
    return [];
  }, [commonDrugPrices]);

  const mapLocations: MapLocation[] = useMemo(() => {
    return pharmacies
      .filter((p) => p.pharmacy_display_rules?.display_on_map !== "never")
      .map((p) => {
        return {
          latitude: p.latitude,
          longitude: p.longitude,
          displayTitle: p.name,
          displayDescription: getPharmacyDescription(
            p as unknown as Pharmacy,
            translate(`${TRANSLATION_PATH}.miles`, { count: p?.distance || 2 }),
          ),
        };
      });
  }, [pharmacies, location]);

  /**
   * TODO -- remove this and push this to the backend so that we're not sorting on
   * only the page here. That is a lot of work tho :(
   */
  const listLocations = useMemo(() => {
    const favoriteProviderIds = (favoritePharmacies?.results || []).map((p) => p.pharmacy.provider_id);
    let sortedPharmacies = commonDrugPrices?.prices;
    if (pageNumber === 1) {
      sortedPharmacies = sortedPharmacies?.slice(0, firstPageResults);
    }
    if (sortBy === "low_to_high") {
      sortedPharmacies = commonDrugPrices?.prices.sort(
        (a, b) =>
          Number(a?.claim?.response?.response_pricing?.patient_pay_amount)
          - Number(b?.claim?.response?.response_pricing?.patient_pay_amount),
      );
    } else if (sortBy === "distance") {
      sortedPharmacies = commonDrugPrices?.prices.sort((a, b) => {
        return a?.pharmacy?.distance - b?.pharmacy?.distance;
      });
    }

    const favoriteFirstPharmacies = (sortedPharmacies || [])
      .map((p, idx) => {
        return {
          ...p,
          originalIndex: idx,
        };
      })
      .sort((p1, p2) => {
        if (favoriteProviderIds.includes(p1.pharmacy.provider_id) && !favoriteProviderIds.includes(p2.pharmacy.provider_id)) {
          return -1;
        } else if (!favoriteProviderIds.includes(p1.pharmacy.provider_id) && favoriteProviderIds.includes(p2.pharmacy.provider_id)) {
          return 1;
        } else {
          return p1.originalIndex - p2.originalIndex;
        }
      });
    return favoriteFirstPharmacies;
  }, [favoritePharmacies, commonDrugPrices, sortBy, firstPageResults]);

  const loadingElements = useMemo(() => {
    return SIX_ARRAY.map((_, idx) => {
      return <LoadingSkeleton key={idx} style={{ width: "100%", height: 64 }} />;
    });
  }, []);

  const onPageChange = useCallback((page: number) => {
    logEvent(`${ANALYTIC_EVENTS.FIND_BEST_PRICE.pagination.pageNumber} ${page}`);
    `1`;
    setPageNumber(page);
  }, []);

  const pager = useMemo(() => {
    return (
      <Pager
        disabled={isLoadingCommonDrugPrices}
        pageNumber={pageNumber}
        setPageNumber={onPageChange}
        resultCount={commonDrugPrices?.result_count}
        firstPageResultCount={firstPageResults}
        resultsPerPage={DEFAULT_COMMON_ELEMENTS_LIMIT}
      />
    );
  }, [isLoadingCommonDrugPrices, pageNumber, setPageNumber, commonDrugPrices]);

  const onBackPressed = useCallback(() => {
    logEvent(ANALYTIC_EVENTS.FIND_BEST_PRICE.backButton);

    const configParams = new URLSearchParams();
    configParams.set("dosage_form", dosageForm);
    configParams.set("dosage", dosage);
    configParams.set("quantity", quantity);
    configParams.set("days_supply", daysSupply);
    configParams.set("loc", searchParams.get("loc"));
    const locationText = searchParams.get("location_text");
    if (locationText) {
      configParams.set("location_text", locationText);
    }

    navigate({
      pathname: `/find-best-price/${drugId}`,
      search: configParams.toString(),
    });
  }, [drugId, dosageForm, dosage, quantity, daysSupply, searchParams]);

  const { allRejectCodes, allPrograms } = useMemo(() => {
    const allRejectCodes = new Set<string>();
    const allPrograms = new Set<string>();
    if (commonDrugPrices) {
      commonDrugPrices.prices.forEach((p) => {
        for (const reject_code of p.claim?.response?.response_status?.reject_codes || []) {
          allRejectCodes.add(reject_code.reject_code);
        }
        for (const program of p.claim?.programs || []) {
          allPrograms.add(program);
        }
      });
    }
    return {
      allRejectCodes: [...allRejectCodes],
      allPrograms: [...allPrograms],
    };
  }, [commonDrugPrices]);

  const hasCopayMaximizerProgram = isCopayMaximizerProgramCheck(allPrograms);

  const onDrugSelected = useCallback(
    (prices: Prices) => {
      const infoParams = new URLSearchParams();
      infoParams.set("dosage_form", dosageForm);
      infoParams.set("dosage", dosage);
      infoParams.set("quantity", quantity);
      infoParams.set("days_supply", daysSupply);
      infoParams.set("loc", searchParams.get("loc"));
      infoParams.set("provider_id", prices.pharmacy.provider_id);
      const submissionClarificationCode = searchParams.get("submission_clarification_code");
      if (submissionClarificationCode != null) {
        infoParams.set("submission_clarification_code", submissionClarificationCode);
      }
      if (hasShownCopayMaxMessage) {
        infoParams.set("copayMaxModalShown", "true");
      }

      const claimStatus = prices.claim?.transmission?.claim_status;
      const basisOfReimbursementCode = prices.claim.response.response_pricing?.basis_of_reimbursement_determination;

      let rejectMessage: string | undefined = undefined;
      const isUsualAndCustomary = basisOfReimbursementCode && basisOfReimbursement[basisOfReimbursementCode] === "usual_and_customary";

      if (claimStatus === "P" && !isUsualAndCustomary) {
        infoParams.set("patient_pay", encodeURIComponent(prices.claim.response.response_pricing.patient_pay_amount || "0"));
        infoParams.set("plan_pay", encodeURIComponent(prices.claim.response.response_pricing.total_amount_paid || "0"));
      } else {
        if (isUsualAndCustomary) {
          rejectMessage = `${translate(`${TRANSLATION_PATH}.thePriceFor`)} ${drug.drug_name} ${translate(`${TRANSLATION_PATH}.ifPurchasedThePrice`)}`;
        } else {
          rejectMessage = prices.claim.response.response_status.reject_message;
        }

        if (!rejectMessage || rejectMessage === "") {
          const additionalMessages = prices.claim.response.response_status.additional_messages;
          if (additionalMessages) {
            rejectMessage = additionalMessages.reduce((message, additional_message) => {
              message += additional_message.additional_message_information;

              // for cases where multiple rejects can come back. add new line
              if (!additional_message.additional_message_information_continuity) {
                message += "\n";
              }

              return message;
            }, "");
          }
        }

        infoParams.set("reject_message", encodeURIComponent(rejectMessage));
      }

      logEvent(ANALYTIC_EVENTS.FIND_BEST_PRICE.resultItems);
      logEvent(ANALYTIC_EVENTS.FIND_BEST_PRICE.selectedPharmacy);

      navigate({
        pathname: `/find-best-price/${drugId}/pricing-info`,
        search: infoParams.toString(),
      });
    },
    [searchParams, drugId, rejectInfo, drug],
  );

  // if there's reject info, pop the modal
  useEffect(() => {
    setShowingRejectModal(rejectInfo.reject_message_list.length > 0 || (hasCopayMaximizerProgram && !hasShownCopayMaxMessage));
  }, [rejectInfo, allPrograms]);

  // if there's custom member messages, pop that modal
  useEffect(() => {
    setShowingMemberMessageModal(memberMessages.length > 0);
  }, [memberMessages]);

  const onModalDismissed = (codesToSuppress: string[], submissionClarificationCode: null | SubmissionClarificationCode) => {
    if (hasCopayMaximizerProgram && !hasShownCopayMaxMessage) {
      setSearchParams((prev) => {
        prev.set("copayMaxModalShown", "true");
        return prev;
      });
    }
    setShowingRejectModal(false);
    if (submissionClarificationCode) {
      setSubmissionClarificationCode(submissionClarificationCode);
    }
    if (codesToSuppress.length > 0) {
      setSuppresableCodes(codesToSuppress);
    }
  };

  const isLoadingData =
    isLoadingCommonDrugPrices
    || (isEnabledMailPharmacySection && isLoadingMailPharmacyPrices)
    || (isEnabledOnlinePharmacySection && isLoadingOnlinePharmacyPrices);

  return (
    <PageWrapper>
      <FindBestPriceRejectModal
        rejectCodes={allRejectCodes}
        hasShownCapRxMessage={!!hasShownCopayMaxMessage}
        programs={allPrograms}
        drug={drug}
        isVisible={showingRejectModal}
        rejectInfo={rejectInfo}
        onDismiss={onModalDismissed}
      />
      <FindBestPriceMemberMessageModal
        onClose={() => setShowingMemberMessageModal(false)}
        customMessages={memberMessages}
        isVisible={showingMemberMessageModal}
      />
      <ContentPanel
        title={
          <DrugHeader drug={commonDrugPrices?.prices?.[0]?.drug} isLoading={isLoadingCommonDrugPrices} onBackPressed={onBackPressed} />
        }
      >
        {!isLoadingCommonDrugsError && (
          <Grid>
            <Grid.Span columns={isCompressed ? 12 : 6}>
              <Stack spacing="large">
                <LoadingGroup loading={isLoadingData}>
                  <LoadingGroup.Placeholder fallback={<Stack spacing="small">{loadingElements}</Stack>}>
                    <Stack spacing="large">
                      <Body>{translate(`${TRANSLATION_PATH}.theMemberResponsibilityMayVary`)}</Body>
                      {isEnabledMailPharmacySection && !isLoadingMailPharmacyError && mailPharmacyPrices?.prices?.length > 0 && (
                        <FindBestPriceResultsSection
                          sectionTitle={<Subheading>{translate(`${TRANSLATION_PATH}.mailOrder`)}</Subheading>}
                          pharmacyDescription={translate(`${TRANSLATION_PATH}.deliveryToYourDoor`)}
                          prices={mailPharmacyPrices?.prices}
                          isLoading={isLoadingMailPharmacyPrices}
                          onDrugSelected={onDrugSelected}
                          getDrugPriceDisplay={getDrugPriceDisplay}
                          hideDisplayTags
                          hideIndex
                        />
                      )}
                      {showOnlinePrices && !isLoadingOnlinePharmacyError && onlinePharmacyPrices?.prices?.length > 0 && (
                        <FindBestPriceResultsSection
                          sectionTitle={
                            <Shelf spacing="small">
                              <Subheading>{translate(`${TRANSLATION_PATH}.online`)}</Subheading>
                              <Tag type="success-high-contrast">{translate(`${TRANSLATION_PATH}.lowestPrice`)}</Tag>
                              <Shelf.Spacer />
                            </Shelf>
                          }
                          pharmacyDescription={translate(`${TRANSLATION_PATH}.orderPrescriptionsOnline`)}
                          prices={onlinePharmacyPrices?.prices}
                          isLoading={isLoadingOnlinePharmacyPrices}
                          onDrugSelected={onDrugSelected}
                          getDrugPriceDisplay={getDrugPriceDisplay}
                          hideDisplayTags
                          hideIndex
                        />
                      )}
                      <FindBestPriceResultsSection
                        sectionTitle={<Subheading>{translate(`${TRANSLATION_PATH}.pharmacyLocations`)}</Subheading>}
                        prices={listLocations}
                        isLoading={isLoadingCommonDrugPrices}
                        onDrugSelected={onDrugSelected}
                        getDrugPriceDisplay={getDrugPriceDisplay}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        hideIndex
                      />
                    </Stack>
                  </LoadingGroup.Placeholder>
                </LoadingGroup>
                {pager}
              </Stack>
            </Grid.Span>

            <Grid.Span columns={isCompressed ? 12 : 6}>
              {!isLoadingCommonDrugPrices && (
                <AppleMap
                  style={{
                    height: isCompressed ? "400px" : "100%",
                  }}
                  renderCalloutContent={(location) => <div>{location.data.displayDescription}</div>}
                  locations={mapLocations.map((location) => ({
                    latitude: parseFloat(location.latitude),
                    longitude: parseFloat(location.longitude),
                    title: location.displayTitle,
                    data: {
                      displayDescription: location.displayDescription,
                    },
                  }))}
                />
              )}
            </Grid.Span>
          </Grid>
        )}
        {isLoadingCommonDrugsError && <NoResults message={translate(`${TRANSLATION_PATH}.anErrorOccured`)} />}
      </ContentPanel>
    </PageWrapper>
  );
};
